/* eslint-disable no-undef */
// @ts-nocheck

import { useEffect, useRef, FC, useState } from 'react';
import clsx from 'clsx';
import styles from './Map.module.scss';

import { LngLat, ExpandedFeature, LngLatBounds } from 'ymaps3';

import { LOCATION, COMMON_LOCATION_PARAMS } from '../../../../../data/map';
import Search from '../../../../Icons/Search';
import { CrossIcon } from '../../../../Icons/Cross';
import Arrow from '../../../../BackButton/Arrow';
import MapIcon from '../../../../Icons/Map';
import List from '../../../../Icons/List';

interface IShop {
  id: string;
  geometry: {
    coordinates: LngLat;
  };
  description: {
    name: string;
    address: string;
    tel: string;
    workingHours: string;
    city: string;
  };
}

export interface IMapProps {
  shops: IShop[];
}

const getListCities = (shops: IShop[]) => {
  const list = [];
  for (let shop of shops) {
    let index = list.findIndex((item) => item.name === shop.description.city);
    if (index === -1)
      list.push({
        name: shop.description.city,
        coordinates: shop.geometry.coordinates,
      });
  }
  return list;
};

const Map: FC<IMapProps> = ({ shops }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);

  const [error, setError] = useState(null);

  const [mapHolder, setMapHolder] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const cities = getListCities(shops);
  const [searchValue, setSearchValue] = useState('');
  const [serchedCities, setSearchedCities] = useState([]);
  const [selectedArrShops, setSelectedArrShops] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState('map'); // list/all

  useEffect(() => {
    const loadYandexMap = () => {
      const script = document.createElement('script');
      script.src = `https://api-maps.yandex.ru/v3/?apikey=${process.env.REACT_APP_YANDEX_MAPS_API_KEY}&lang=ru_RU`;
      script.onload = async () => {
        if (ymaps3) {
          await ymaps3.ready;
          ymaps3.import.registerCdn('https://cdn.jsdelivr.net/npm/{package}', [
            '@yandex/ymaps3-default-ui-theme@0.0.19',
            '@yandex/ymaps3-clusterer@0.0.10',
          ]);
          try {
            const {
              YMap,
              YMapDefaultSchemeLayer,
              YMapDefaultFeaturesLayer,
              YMapMarker,
              YMapControls,
            } = ymaps3;
            const map = new YMap(mapRef.current, {
              location: LOCATION,
            });
            setMapHolder(map);
            map.addChild(new YMapDefaultSchemeLayer({}));
            map.addChild(new YMapDefaultFeaturesLayer({}));

            const marker = (feature: ExpandedFeature) => {
              const markerContainerElement = document.createElement('div');
              markerContainerElement.classList.add('marker-container');

              const markerText = document.createElement('div');
              markerText.id = feature.id;
              markerText.classList.add('marker-text', 'hidden');
              markerText.innerHTML = `<h4>${feature.description.name}</h4><span><img src='./icons/pin.svg'/>${feature.description.address}</span><span><img src='./icons/phone.svg'/>${feature.description.tel}</span>`;

              markerContainerElement.addEventListener('click', () => {
                map.update({
                  location: {
                    center: feature.geometry.coordinates,
                    zoom: 15,
                    ...COMMON_LOCATION_PARAMS,
                  },
                });
                setTimeout(() => {
                  markerText.classList.remove('hidden');
                  markerText.classList.add('visible');
                }, 1500);
              });
              document.addEventListener('click', (e) => {
                if (!markerContainerElement.contains(e.target as Node)) {
                  markerText.classList.add('hidden');
                  markerText.classList.remove('visible');
                }
              });

              const markerElement = document.createElement('div');
              markerElement.classList.add('marker');

              const markerImage = document.createElement('img');
              markerImage.src = './icons/marker.svg';
              markerImage.classList.add('image');

              markerElement.appendChild(markerImage);

              markerContainerElement.appendChild(markerText);
              markerContainerElement.appendChild(markerElement);

              return new YMapMarker(
                {
                  coordinates: feature.geometry.coordinates,
                },
                markerContainerElement,
              );
            };

            const getBounds = (coordinates: number[][]): LngLatBounds => {
              let minLat = Infinity,
                minLng = Infinity;
              let maxLat = -Infinity,
                maxLng = -Infinity;

              for (const coords of coordinates) {
                const lat = coords[1];
                const lng = coords[0];

                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
                if (lng < minLng) minLng = lng;
                if (lng > maxLng) maxLng = lng;
              }

              return [
                [minLng - 0.01, minLat - 0.01],
                [maxLng + 0.01, maxLat + 0.01],
              ] as LngLatBounds;
            };

            const cluster = (
              coordinates: LngLat,
              features: ExpandedFeature[],
            ) =>
              new YMapMarker(
                {
                  coordinates,
                  onClick() {
                    const bounds = getBounds(
                      features.map(
                        (feature: ExpandedFeature[]) =>
                          feature.geometry.coordinates,
                      ),
                    );
                    map.update({
                      location: { bounds, ...COMMON_LOCATION_PARAMS },
                    });
                  },
                },
                circle(features.length).cloneNode(true) as HTMLElement,
              );

            const circle = (count: number) => {
              const circle = document.createElement('div');
              circle.classList.add('circle');
              circle.innerHTML = `
                            <div class="circle-content">
                                <span class="circle-text">${count}</span>
                            </div>
                        `;
              return circle;
            };

            const { clusterByGrid, YMapClusterer } = await ymaps3.import(
              '@yandex/ymaps3-clusterer',
            );
            const clusterer = new YMapClusterer({
              method: clusterByGrid({ gridSize: 64 }),
              features: shops,
              marker,
              cluster,
            });
            map.addChild(clusterer);
            const { YMapZoomControl } = await ymaps3.import(
              '@yandex/ymaps3-default-ui-theme',
            );
            const controls = new YMapControls({ position: 'right' }).addChild(
              new YMapZoomControl({}),
            );
            map.addChild(controls);

            setError(null);
          } catch (initError) {
            setError(`Ошибка инициализации карты: ${initError.message}`);
          }
        } else {
          setError('Ошибка загрузки карты');
          console.error('Ошибка загрузки Яндекс.Карт: модуль ymaps3 не найден');
        }
        setMapLoaded(true);
      };
      script.onerror = () => {
        setError('Ошибка загрузки карты');
        console.error('Ошибка загрузки скрипта Яндекс.Карт');
      };
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    };

    if (!mapLoaded) {
      loadYandexMap();
    }

    const resizeHandler = () => {
      if (window.innerWidth < 1024 && view !== 'map') setView('map');
      if (window.innerWidth >= 1024 && view !== 'all') setView('all');
    };

    resizeHandler();

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const updateListSerachedCities = (value) => {
    if (value.length) {
      setSearchValue(value);
      setSearchedCities(
        cities.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else {
      setSearchValue('');
      setSearchedCities([]);
    }
  };

  const setCurrentCity = (city) => {
    setSearchValue(city.name);
    setSearchedCities([]);
    setSelectedArrShops(
      shops.filter((item) => item.description.city === city.name),
    );
    if (mapHolder) {
      mapHolder.update({
        location: {
          center: city.coordinates,
          zoom: 9,
          ...COMMON_LOCATION_PARAMS,
        },
      });
    }
    if (window.innerWidth < 1024) {
      setView('list');
    }
  };

  const setCurrentShop = (shop) => {
    if (mapHolder) {
      mapHolder.update({
        location: {
          center: shop.geometry.coordinates,
          zoom: 15,
          ...COMMON_LOCATION_PARAMS,
        },
      });
    }
    if (window.innerWidth < 1024) {
      setView('map');
    }
  };

  const clearCurrentCity = () => {
    setSearchValue('');
    setSearchedCities([]);
    setSelectedArrShops([]);
    if (window.innerWidth < 1024) {
      setView('map');
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      <div className={styles.Map}>
        <div className={styles.Map_search}>
          <input
            type="text"
            placeholder="Поиск по городу и адресу"
            name="search"
            value={searchValue}
            onChange={(e) => updateListSerachedCities(e.target.value)}
            onClick={() => window.innerWidth < 1024 && setModalOpen(true)}
          />
          <Search />
          {searchValue && (
            <button
              className={styles.Map_search_clear}
              onClick={() => clearCurrentCity()}
            >
              <CrossIcon />
            </button>
          )}
          {serchedCities.length ? (
            <ul>
              {serchedCities.map((city) => (
                <li key={city.name} onClick={() => setCurrentCity(city)}>
                  {city.name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {selectedArrShops.length ? (
          <div className={styles.Map_switch_btns}>
            <button
              className={clsx({ [styles.active]: view === 'map' })}
              onClick={() => setView('map')}
            >
              <MapIcon />
              Карта
            </button>
            <button
              className={clsx({ [styles.active]: view === 'list' })}
              onClick={() => setView('list')}
            >
              <List />
              Список
            </button>
          </div>
        ) : null}

        <div className={styles.Map_container}>
          {selectedArrShops.length && view !== 'map' ? (
            <div className={styles.Map_shops}>
              <div className={styles.Map_shops_holder}>
                <ul>
                  {selectedArrShops.map((shop) => (
                    <li key={shop.id} onClick={() => setCurrentShop(shop)}>
                      <h4>{shop.description.name}</h4>
                      <span>
                        <img src="./icons/pin.svg" />
                        {shop.description.address}
                      </span>
                      <span>
                        <img src="./icons/phone.svg" />
                        {shop.description.tel}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
          <div
            className={clsx(styles.Map_holder, {
              [styles.Map_holder_show]: view === 'map' || view === 'all',
            })}
            style={{ width: '100%', height: '600px' }}
            ref={mapRef}
          ></div>
        </div>
      </div>
      {modalOpen && (
        <div className={styles.Map_modal}>
          <div className={styles.modal_header}>
            <button onClick={() => setModalOpen(false)}>
              <Arrow />
            </button>
            <div className={styles.search_holder}>
              <input
                type="text"
                placeholder="Поиск по городу и адресу"
                name="search"
                value={searchValue}
                onChange={(e) => updateListSerachedCities(e.target.value)}
                onClick={() => window.innerWidth < 1024 && setModalOpen(true)}
              />
              <Search />
              {searchValue && (
                <button
                  className={styles.Map_search_clear}
                  onClick={() => clearCurrentCity()}
                >
                  <CrossIcon />
                </button>
              )}
            </div>
          </div>
          {serchedCities.length ? (
            <ul>
              {serchedCities.map((city) => (
                <li
                  key={city.name}
                  onClick={() => {
                    setCurrentCity(city);
                    setModalOpen(false);
                  }}
                >
                  {city.name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </>
  );
};

export default Map;
